import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { BRAND } from '../brand';

const EyeIcon = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {open ? (
      <>
        <path d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12s-3.5 6.5-9.5 6.5S2.5 12 2.5 12Z" />
        <circle cx="12" cy="12" r="2.8" />
      </>
    ) : (
      <>
        <path d="M4 4l16 16" />
        <path d="M9.9 5.9A10.4 10.4 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.4 3.2M6.2 7.1A16.6 16.6 0 0 0 2.5 12S6 18.5 12 18.5c1.2 0 2.3-.2 3.3-.6" />
      </>
    )}
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <path d="m4.5 8 6.4 4.6a2 2 0 0 0 2.2 0L19.5 8" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4.5" y="10" width="15" height="10.5" rx="3" />
    <path d="M8 10V7.8a4 4 0 0 1 8 0V10" />
    <path d="M12 14.2v2.3" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12h13" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2.8 13.9 9l5.8 1.9-5.8 1.9L12 19l-1.9-6.2L4.3 10.9 10.1 9 12 2.8Z" />
    <path d="M19 16.5l.7 2.1 2.1.7-2.1.7-.7 2.1-.7-2.1-2.1-.7 2.1-.7.7-2.1Z" opacity=".7" />
  </svg>
);

const features = [
  { no: '01', label: '梅花', title: '一卦一断', text: '体用、互变、旺衰，按梅花次第展开。' },
  { no: '02', label: '奇门', title: '九宫读象', text: '用神落宫、门星神干，合参主客四害。' },
  { no: '03', label: '八字', title: '四柱批命', text: '十神、大运、流年，落到可执行建议。' },
];

export const SignInPage: React.FC = () => {
  const { signIn, signUp, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const switchMode = (next: boolean) => {
    setIsSignUp(next);
    setError(null);
    setNotice(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!email || !password) {
      setError('请输入邮箱和密码');
      return;
    }
    setLoading(true);
    try {
      if (isSignUp) {
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }
      const redirectTo = (location.state as any)?.from?.pathname || '/app/meihua';
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err?.message || '登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const normalizedEmail = email.trim();
    setError(null);
    setNotice(null);
    if (!normalizedEmail) {
      setError('请输入邮箱地址');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(normalizedEmail);
      setNotice('如果该邮箱已注册，密码重置邮件已经发送。');
    } catch (err: any) {
      if (err?.code === 'auth/invalid-email') {
        setError('邮箱格式不正确');
      } else {
        setError(err?.message || '无法发送重置邮件，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-v2">
      <div className="auth-v2-bg" aria-hidden>
        <span className="auth-v2-orb auth-v2-orb--gold" />
        <span className="auth-v2-orb auth-v2-orb--jade" />
        <span className="auth-v2-grid" />
      </div>

      <div className="auth-v2-shell">
        <aside className="auth-v2-story">
          <div className="auth-v2-brand">
            <div className="auth-v2-mark" aria-hidden>
              <span className="auth-v2-mark-ring" />
              <span className="auth-v2-seal">{BRAND.seal}</span>
            </div>
            <div className="auth-v2-brand-text">
              <h1>{BRAND.name}</h1>
              <p>{BRAND.english}</p>
            </div>
          </div>

          <div className="auth-v2-copy">
            <span className="auth-v2-kicker">
              <SparkIcon />
              {BRAND.tagline}
            </span>
            <h2>
              把一次起念，
              <br />
              <em>读成一条路。</em>
            </h2>
            <p>梅花、奇门、八字三套术数，同一套排盘与 AI 解读。先看清局面，再决定下一步。</p>
          </div>

          <div className="auth-v2-features">
            {features.map((feature) => (
              <article key={feature.label} className="auth-v2-feature">
                <span className="auth-v2-feature-no">{feature.no}</span>
                <span className="auth-v2-feature-label">{feature.label}</span>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>

          <div className="auth-v2-story-foot">
            <span>排盘免费</span>
            <i />
            <span>AI 依术数次第解读</span>
          </div>
        </aside>

        <section className="auth-v2-panel" aria-label="登录与注册">
          <div className="auth-v2-card">
            <header className="auth-v2-card-head">
              <span className="auth-v2-eyebrow">{isSignUp ? '开启观象' : '欢迎回来'}</span>
              <h2>{isSignUp ? '创建你的账号' : '登录观象'}</h2>
              <p>{isSignUp ? '注册即享免费 AI 解读额度，排盘永久免费。' : '继续你的求测记录、卦盘与解读。'}</p>
            </header>

            <div className="auth-v2-tabs" role="tablist" aria-label="登录或注册">
              <button
                type="button"
                role="tab"
                aria-selected={!isSignUp}
                className={!isSignUp ? 'is-active' : ''}
                onClick={() => switchMode(false)}
              >
                登录
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isSignUp}
                className={isSignUp ? 'is-active' : ''}
                onClick={() => switchMode(true)}
              >
                注册
              </button>
            </div>

            <form className="auth-v2-form" onSubmit={handleSubmit}>
              <label className="auth-v2-field" htmlFor="auth-email">
                <span className="auth-v2-field-label">邮箱</span>
                <span className="auth-v2-input">
                  <span className="auth-v2-input-icon"><MailIcon /></span>
                  <input
                    id="auth-email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                  />
                </span>
              </label>

              <label className="auth-v2-field" htmlFor="auth-password">
                <span className="auth-v2-field-label">密码</span>
                <span className="auth-v2-input">
                  <span className="auth-v2-input-icon"><LockIcon /></span>
                  <input
                    id="auth-password"
                    placeholder={isSignUp ? '至少 6 位字符' : '输入密码'}
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  />
                  <button
                    type="button"
                    className="auth-v2-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? '隐藏密码' : '显示密码'}
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </span>
              </label>

              <div className="auth-v2-row">
                <span className="auth-v2-safe">安全登录 · 记录仅归你所有</span>
                {!isSignUp && (
                  <button type="button" className="auth-v2-forgot" onClick={handlePasswordReset} disabled={loading}>
                    忘记密码？
                  </button>
                )}
              </div>

              {error && <div className="mei-error auth-v2-message">{error}</div>}
              {notice && <div className="auth-v2-notice">{notice}</div>}

              <button className="auth-v2-submit" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="mei-spinner" />
                    <span>请稍候</span>
                  </>
                ) : (
                  <>
                    <span>{isSignUp ? '创建账号' : '进入观象'}</span>
                    <span className="auth-v2-submit-arrow"><ArrowIcon /></span>
                  </>
                )}
              </button>
            </form>

            <p className="auth-v2-fineprint">{BRAND.descriptor} · AI 解读仅供传统文化参考</p>
          </div>

          <div className="auth-v2-panel-foot">
            <span>{BRAND.name} · {BRAND.english}</span>
            <i />
            <span>{BRAND.tagline}</span>
          </div>
        </section>
      </div>
    </div>
  );
};
